import {appendTrainingTypesMatrixPDF} from "./appendTrainingTypesMatrixPDF";
import {appendTrainingTypesPDF} from "./appendTrainingTypesPDF";
import {jsPDF} from "jspdf";
import moment from "moment";
import matrixRegularFont from "@/assets/fonts/BarlowCondensed-Medium.ttf";
import matrixBoldFont from "@/assets/fonts/BarlowCondensed-Bold.ttf";
import {
	MATRIX_STATUSES,
	MATCH_MATRIX_SUMMARY_METRICS,
	matrixSummaryMetric,
	summarizeMatrixRow,
} from "./rosterActivityMatrix";

let matrixFontsPromise;
const loadMatrixFonts = () => {
	if (!matrixFontsPromise) {
		matrixFontsPromise = Promise.all(
			[matrixRegularFont, matrixBoldFont].map(async (url) => {
				const response = await fetch(url);
				if (!response.ok)
					throw new Error("Impossibile caricare il carattere PDF.");
				const bytes = new Uint8Array(await response.arrayBuffer());
				let binary = "";
				for (let i = 0; i < bytes.length; i += 8192) {
					binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
				}
				return btoa(binary);
			}),
		).catch((error) => {
			matrixFontsPromise = undefined;
			throw error;
		});
	}
	return matrixFontsPromise;
};

const clean = (value) =>
	String(value ?? "")
		.replace(/[‐-―−]/g, "-")
		.replace(/′/g, "'")
		.replace(/·/g, " | ");
const colors = {
	scheduled: [237, 241, 248],
	present: [232, 244, 236],
	substitute: [234, 242, 255],
	absent: [241, 243, 246],
	injured: [255, 240, 233],
	bench: [245, 240, 223],
	unknown: [240, 245, 250],
	na: [250, 251, 252],
	check: [255, 243, 215],
};

export async function createRosterMatrixPdf({
	data,
	rows,
	kind,
	fromDate,
	toDate,
	search,
	competitionLabel,
	trainingScope = "completed",
	trainingMetric = "minutes",
	trainingView = "attendance",
}) {
	const [regularFont, boldFont] = await loadMatrixFonts();
	const doc = new jsPDF({orientation: "landscape", unit: "mm", format: "a4"});
	doc.addFileToVFS("Matrix-Regular.ttf", regularFont);
	doc.addFont("Matrix-Regular.ttf", "Matrix", "normal");
	doc.addFileToVFS("Matrix-Bold.ttf", boldFont);
	doc.addFont("Matrix-Bold.ttf", "Matrix", "bold");
	doc.setFont("Matrix", "normal");
	const columns = data?.columns || [];
	const margin = 12,
		width = doc.internal.pageSize.getWidth() - margin * 2,
		nameWidth = 46,
		rowHeight = 5,
		headerHeight = 13,
		top = 32;
	const summaryWidths =
		kind === "training" ? [18, 13, 17] : [18, 15, 14, 10, 10, 10, 15];
	const summaryWidth = summaryWidths.reduce((sum, v) => sum + v, 0);
	const maxColumns = Math.max(
		1,
		Math.floor((width - nameWidth - summaryWidth) / 11),
	);
	const columnsPerPage = Math.ceil(
		columns.length / Math.max(1, Math.ceil(columns.length / maxColumns)),
	);
	const maxRows = Math.floor(
		(doc.internal.pageSize.getHeight() - 20 - top - headerHeight) / rowHeight,
	);
	const label =
		kind === "training" ? "Allenamenti - presenze" : "Partite - impiego";
	const fitted = (value, w, lines = 1) =>
		doc.splitTextToSize(clean(value), w - 2).slice(0, lines);
	const box = (x, y, w, h, fill) => {
		doc.setFillColor(...fill);
		doc.setDrawColor(217, 226, 237);
		doc.setLineWidth(0.15);
		doc.rect(x, y, w, h, "FD");
	};
	const typesView = kind === "training" && trainingView === "types";
	let page = typesView
		? appendTrainingTypesMatrixPDF(
				doc,
				data,
				rows,
				search,
				clean,
				trainingMetric,
				trainingScope,
			)
		: 0;
	for (
		let offset = 0;
		!typesView && offset < columns.length;
		offset += columnsPerPage
	) {
		const block = columns.slice(offset, offset + columnsPerPage),
			columnWidth = (width - nameWidth - summaryWidth) / block.length;
		for (let firstRow = 0; firstRow < rows.length; firstRow += maxRows) {
			if (page++) doc.addPage();
			doc.setTextColor(18, 48, 83);
			doc.setFont("Matrix", "bold");
			doc.setFontSize(12);
			doc.text(clean(data.rosterName + " | " + label), margin, 12);
			doc.setFont("Matrix", "normal");
			doc.setFontSize(8);
			doc.text(
				clean(
					"Stagione " +
						data.seasonName +
						" | " +
						moment(fromDate).format("DD/MM/YYYY") +
						" - " +
						moment(toDate).format("DD/MM/YYYY"),
				),
				margin,
				18,
			);
			const filters = [
				search ? "Ricerca: " + search : null,
				kind === "match" ? competitionLabel : null,
				"Totali riferiti a tutta la selezione",
			]
				.filter(Boolean)
				.join(" | ");
			doc.text(fitted(filters, width), margin, 23);
			doc.setFontSize(6.5);
			doc.setTextColor(102, 120, 143);
			doc.text(
				`Colonne ${offset + 1}-${offset + block.length}/${columns.length} | Giocatori ${firstRow + 1}-${Math.min(firstRow + maxRows, rows.length)}/${rows.length}`,
				margin,
				28,
			);
			box(margin, top, nameWidth, headerHeight, [239, 245, 252]);
			doc.setTextColor(23, 57, 95);
			doc.setFontSize(7);
			doc.setFont("Matrix", "bold");
			doc.text("Giocatore", margin + 2, top + 7);
			let x = margin + nameWidth;
			for (const c of block) {
				box(x, top, columnWidth, headerHeight, [239, 245, 252]);
				doc.setFontSize(6.1);
				doc.text(moment(c.date).format("DD/MM"), x + columnWidth / 2, top + 3, {
					align: "center",
				});
				doc.setFont("Matrix", "normal");
				const info =
					kind === "training"
						? [
								moment(c.date).format("HH:mm"),
								...(c.scheduled ? ["In programma"] : []),
							]
						: [
								c.matchSide === "home" ? "Casa" : "Trasferta",
								...fitted(c.opponent || c.label, columnWidth, 2),
								c.scheduled
									? c.timeToBeConfirmed
										? "Prog. - OdD"
										: "Prog. " + moment(c.date).format("HH:mm")
									: c.result || "-",
							];
				doc.text(info.map(clean), x + columnWidth / 2, top + 5.8, {
					align: "center",
					lineHeightFactor: 1.05,
				});
				x += columnWidth;
			}
			const headings =
				kind === "training"
					? ["Presenze", "%", "Da rilev."]
					: [
							"Giocate",
							"Minuti",
							...MATCH_MATRIX_SUMMARY_METRICS.map(([, label]) => label),
							"Da rilev.",
						];
			summaryWidths.forEach((w, i) => {
				box(x, top, w, headerHeight, [239, 245, 252]);
				doc.text(headings[i], x + w / 2, top + 7, {align: "center"});
				x += w;
			});
			rows.slice(firstRow, firstRow + maxRows).forEach((player, i) => {
				const y = top + headerHeight + i * rowHeight;
				doc.setFontSize(6.5);
				doc.setFont("Matrix", "normal");
				doc.setTextColor(23, 57, 95);
				box(
					margin,
					y,
					nameWidth,
					rowHeight,
					i % 2 ? [247, 249, 252] : [255, 255, 255],
				);
				doc.text(
					fitted(
						(player.number ? "#" + player.number + " " : "") +
							player.name +
							(player.guest ? " *" : ""),
						nameWidth,
					),
					margin + 1,
					y + 3.3,
				);
				let xx = margin + nameWidth;
				for (const c of block) {
					const cell = player.cells[c.key] || {status: "unrecorded"},
						status = MATRIX_STATUSES[cell.status] || MATRIX_STATUSES.unrecorded;
					box(
						xx,
						y,
						columnWidth,
						rowHeight,
						colors[status.tone] || colors.unknown,
					);
					const main =
						status.short +
						(kind === "match" && cell.minutes != null
							? " " + cell.minutes + "'"
							: "");
					const events = [
						cell.goals > 0 ? "G" + cell.goals : null,
						cell.assists > 0 ? "A" + cell.assists : null,
						cell.yellowCards > 0 ? "Y" + cell.yellowCards : null,
						cell.redCards > 0 ? "R" + cell.redCards : null,
						cell.warning ? "!" : null,
					]
						.filter(Boolean)
						.join(" ");
					doc.setFontSize(6.2);
					doc.text(
						clean(main),
						xx + columnWidth / 2,
						y + (events ? 2.2 : 3.3),
						{align: "center"},
					);
					if (events) {
						doc.setFontSize(5.1);
						doc.text(
							fitted(events, columnWidth),
							xx + columnWidth / 2,
							y + 4.3,
							{align: "center"},
						);
					}
					xx += columnWidth;
				}
				const totals = summarizeMatrixRow(player, kind);
				const values =
					kind === "training"
						? [
								totals.attended + "/" + totals.total,
								totals.total
									? Math.round((totals.attended / totals.total) * 100) + "%"
									: "-",
								totals.unknown,
							]
						: [
								totals.attended + "/" + totals.total,
								totals.minutes ?? "-",
								...MATCH_MATRIX_SUMMARY_METRICS.map(([key]) => {
									const metric = matrixSummaryMetric(player, totals, key);
									return (
										String(metric.value ?? "-") +
										(metric.conceded ? " sub." : "")
									);
								}),
								totals.unknown,
							];
				doc.setFontSize(6.5);
				summaryWidths.forEach((w, j) => {
					box(xx, y, w, rowHeight, [246, 249, 253]);
					doc.text(String(values[j]), xx + w / 2, y + 3.3, {align: "center"});
					xx += w;
				});
			});
		}
	}
	const trainingStart =
		kind === "training"
			? appendTrainingTypesPDF(
					doc,
					data,
					trainingScope,
					trainingMetric,
					clean,
					page === 0,
				)
			: null;
	for (let n = 1; n <= doc.getNumberOfPages(); n++) {
		doc.setPage(n);
		doc.setFont("Matrix", "normal");
		doc.setFontSize(6.2);
		doc.setTextColor(92, 111, 135);
		const legend =
			trainingStart && n >= trainingStart
				? "Tipologie: conteggi per seduta distinta e minuti dei dettagli registrati. Sedute concluse e programmate separate."
				: typesView
					? trainingScope === "scheduled"
						? "Allenamenti programmati: dati individuali non ancora disponibili, barre allo 0%. Presenze precompilate escluse fino alla conclusione."
						: "Tipologie per giocatore: minuti e sedute / totale rosa. Barre e percentuali riferite alle sole sedute concluse."
					: kind === "training"
						? "P Presente | A Assente | INF Infortunato | PR Programmato (escluso dai totali) | ? Non rilevato | ! Da verificare | - Non pertinente"
						: "T Titolare | S Subentrato | P Panchina | D Distinta incompleta | C Convocato | NC Non convocato | INF Infortunato | PR Programmata (esclusa dai totali) | ? Non rilevato | ! Da verificare";
		doc.text(legend, margin, doc.internal.pageSize.getHeight() - 13);
		doc.text(
			trainingStart && n >= trainingStart
				? "* Durata parziale o non rilevata. Dati mancanti distinti da zero."
				: typesView
					? "* Dati parziali/da verificare; accanto al nome: partecipante fuori rosa. - Dato non rilevato. Zero distinto da presenza mancante."
					: kind === "training"
						? "* Partecipante fuori dalla rosa attuale. Dati mancanti distinti da assenze e zero."
						: "* Partecipante fuori dalla rosa attuale. G Goal | A Assist | Y Gialli | R Espulsioni. Colonna Goal: subiti per portieri (sub.). Dati mancanti diversi da zero.",
			margin,
			doc.internal.pageSize.getHeight() - 9,
		);
		doc.text(
			`${n}/${doc.getNumberOfPages()}`,
			doc.internal.pageSize.getWidth() - margin,
			doc.internal.pageSize.getHeight() - 5,
			{align: "right"},
		);
	}
	return doc;
}
export async function generateRosterMatrixPDF(options) {
	const doc = await createRosterMatrixPdf(options);
	const name = String(options.data.rosterName).replace(/[^a-z0-9]+/gi, "_");
	doc.save(
		`Matrice_${options.kind}_${name}_${options.fromDate}_${options.toDate}.pdf`,
	);
}
